"use client";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { iconsMap } from "@/components/icons";
import { Reveal } from "@/components/reveal";
import { PortableText, PortableTextReactComponents } from "next-sanity";
import { ValuePropositionType } from "./community";
import { Check } from "@/components/icons/check";
import { getURL } from "@/lib/utils";
import { SanityImage } from "@/components/sanity-image";

interface ValuePropositionPros {
  index?: number;
  data: ValuePropositionType;
}

export const components: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => (
      <p className="text-start text-xs sm:text-base text-primary-text-400 leading-7 max-w-xl">
        {children}.
      </p>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="ml-0 mb-2 list-disc [&>li]:mt-2">{children}</ul>,
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="list-none flex flex-row gap-2 leading-7">
        <div className="text-lg border-primary-500 rounded-full border w-8 h-8 text-primary-500 flex items-center justify-center shrink-0">
          <Check />
        </div>
        <p className="leading-7 text-primary-text-900 dark">{children}</p>
      </li>
    ),
  },
};

export function FeatureValueProp({ data, index = 0 }: ValuePropositionPros) {
  const { title, subtitle, description, image, ctas, features } = data;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isEven = index % 2 == 0;
  return (
    <section
      data-variant={isEven ? "primary" : "secondary"}
      className="py-16 md:py-20 lg:py-24  w-full data-[variant=secondary]:bg-secondary-50 data-[variant=primary]:bg-primary-50"
    >
      <div className="max-w-screen-2xl w-full mx-auto px-6 md:px-8">
        <Reveal className="grid grid-cols-1 md:grid-cols-[1fr_auto] lg:grid-cols-[auto_auto] gap-3 md:gap-6 lg:gap-10 items-center justify-center shadow-[0px_2.24px_5.59px_0px_rgba(0,_0,_0,_0.1)] md:shadow-none lg:shadow-none rounded-2xl md:rounded-none lg:rounded-none p-2 md:p-0 lg:p-0 mx-auto bg-white md:bg-transparent lg:bg-transparent max-w-[500px] md:max-w-none lg:max-w-screen-2xl">
          <div className="w-full max-w-[400px] md:max-w-none lg:max-w-max flex flex-col gap-2 sm:gap-4 px-4 pb-4 md:px-0 md:pb-0 lg:px-0 lg:pb-0">
            <h4 className="font-medium text-sm md:text-2xl lg:text-3xl md:text-primary lg:text-primary">
              {title}
            </h4>
            {subtitle && (
              <h4 className="font-medium text-xs sm:text-sm md:text-base lg:text-base">
                {subtitle}
              </h4>
            )}
            {description && <PortableText value={description} components={components} />}
            {features && (
              <ul className="ml-0 my-6 list-disc [&>li]:mt-2">
                {features.map((feat) => (
                  <li className="list-none flex flex-row gap-2 leading-7" key={feat._key}>
                    <div className="text-lg border-primary-500 rounded-full border w-8 h-8 text-primary-500 flex items-center justify-center shrink-0">
                      <Check />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="leading-7 text-primary-text-900 font-medium">{feat.title}</h3>
                      {feat.description && (
                        <PortableText value={feat.description} components={components} />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-4 md:pt-6 lg:pt-6 flex flex-row gap-4">
              {ctas &&
                ctas.map((cta, index) => {
                  const href = getURL(cta);
                  return (
                    <Button
                      variant={cta.variant || "default"}
                      radius="full"
                      className="w-fit"
                      size={isMobile ? "sm" : "lg"}
                      asChild={true}
                      key={index}
                    >
                      <Link
                        href={href}
                        target={cta.openInNewTab ? "_blank" : "_self"}
                        rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                      >
                        {cta.textBeforeIcon}
                        {cta.icon && iconsMap[cta.icon]}
                        {cta.textAfterIcon}
                      </Link>
                    </Button>
                  );
                })}
            </div>
          </div>
          <div
            data-first={isEven}
            className="w-full md:w-full md:max-w-[350px] lg:w-[549px] lg:max-w-none row-start-1 md:col-start-2 lg:col-start-2 data-[first=true]:md:col-start-1 data-[first=true]:lg:col-start-1"
          >
            <AspectRatio
              ratio={549 / 463}
              className="bg-neutral-800/80 rounded-t-2xl md:rounded-2xl lg:rounded-b-2xl"
            >
              {image && image.asset && (
                <SanityImage
                  src={image}
                  alt={image.alt || ""}
                  fill
                  placeholder="blur"
                  blurDataURL={image.asset?.metadata?.lqip || ""}
                  className="object-cover rounded-t-2xl md:rounded-2xl lg:rounded-b-2xl cursor-pointer"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </AspectRatio>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
